import { createFileRoute, useSearch, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useCanEditRecipes } from "@/hooks/useCanEditRecipes";
import { useRecipeDialogs } from "@/hooks/useRecipeDialogs";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { RecipesPageSkeleton } from "@/components/recipes";
import { recipesSearchSchema } from "@/components/recipes/tabs/search-schema";
import {
  RecipesHeader,
  RecipesListing,
  type RecipesListingSearchParams,
} from "@/components/recipes";

export const Route = createFileRoute("/(protected)/_layout/recipes/")({
  validateSearch: (search) => recipesSearchSchema.parse(search),
  component: RecipesPage,
});

function RecipesPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const dialogs = useRecipeDialogs();
  const searchParams = useSearch({ from: "/(protected)/_layout/recipes/" });
  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const canEditRecipes = useCanEditRecipes();

  const onNavigate = (updater: (prev: RecipesListingSearchParams) => RecipesListingSearchParams) =>
    void navigate({ from: "/recipes/", search: updater, replace: true });

  if (isLoading || !isAuthenticated) return <RecipesPageSkeleton />;

  const onIngredientsTab = searchParams.tab === "ingredients";

  // Recipes are created and imported in Build mode only (YC-33), so in Working
  // mode those entries are hidden, not disabled. The Ingredients tab is not
  // covered by that rule: it still imports ingredients, and its Create goes
  // straight to the ingredient form since the chooser would only offer recipes.
  const headerProps = {
    onExport: () => dialogs.open("exportRecipes"),
    // The Ingredients tab gets the column-mapping import wizard (T-1201); the
    // recipe tabs keep the round-trip importer that reads our own export.
    onImport:
      onIngredientsTab || canEditRecipes
        ? () => dialogs.open(onIngredientsTab ? "importIngredients" : "importRecipes")
        : undefined,
    onShare: () => dialogs.open("shareRecipes"),
    onCreateNew: canEditRecipes
      ? () => dialogs.open("createNew")
      : onIngredientsTab
        ? () => dialogs.open("createMaterial")
        : undefined,
  };

  return (
    <div className="flex flex-col gap-5 py-5 px-3">
      {isTablet ? (
        <PageHeader>
          <RecipesHeader tablet {...headerProps} />
        </PageHeader>
      ) : (
        <RecipesHeader {...headerProps} />
      )}
      <RecipesListing
        searchParams={searchParams}
        onNavigate={onNavigate}
        dialogs={dialogs}
      />
    </div>
  );
}
